/*
 * Shared building blocks for the sales task screens (list, form, popups, tabs).
 * One look everywhere: values 13px, secondary 12px, meta 11.5px, chips 11px,
 * uppercase labels 10.5px; colours only from theme tokens so dark mode follows.
 * Buttons use inline styles because the CMS's global `button {}` rule wins over classes.
 */
import React, { useEffect, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './sales_task.css';

export type Tone = 'neutral' | 'success' | 'warning' | 'error' | 'info' | 'outline' | 'brand';

const TONE: Record<Tone, CSSProperties> = {
  neutral: { background: 'var(--surface-sunken)', color: 'var(--text-secondary)' },
  success: { background: 'var(--state-success-bg)', color: 'var(--state-success-text)' },
  warning: { background: 'var(--state-warning-bg)', color: 'var(--state-warning-text)' },
  error: { background: 'var(--state-error-bg)', color: 'var(--state-error-text)' },
  info: { background: 'var(--state-info-bg)', color: 'var(--state-info-text)' },
  outline: {
    background: 'var(--surface-card)',
    color: 'var(--text-secondary)',
    boxShadow: 'inset 0 0 0 1px var(--border-subtle)'
  },
  brand: { background: 'var(--brand-primary-soft)', color: 'var(--text-link-brand)' }
};

export const toneStyle = (tone: Tone): CSSProperties => TONE[tone];

export function Chip({
  tone = 'neutral',
  title,
  children,
  testId
}: {
  tone?: Tone;
  title?: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <span
      title={title}
      data-testid={testId}
      className="inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold leading-4"
      style={TONE[tone]}
    >
      {children}
    </span>
  );
}

// Uppercase section / column label.
export const LABEL_CLASS = 'text-[10.5px] font-bold uppercase tracking-[.04em] text-content-muted';

export type BtnVariant = 'primary' | 'outline' | 'neutral' | 'ghost' | 'danger';
export type BtnSize = 'row' | 'md' | 'touch';

const BTN_VARIANT: Record<BtnVariant, CSSProperties> = {
  primary: {
    background: 'var(--brand-primary)',
    color: 'var(--text-on-brand)',
    border: '1px solid var(--brand-primary)',
    fontWeight: 600
  },
  outline: {
    background: 'var(--surface-card)',
    color: 'var(--text-link-brand)',
    border: '1px solid var(--brand-primary)',
    fontWeight: 600
  },
  neutral: {
    background: 'var(--surface-card)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border-subtle)',
    fontWeight: 500
  },
  ghost: {
    background: 'transparent',
    color: 'var(--text-link-brand)',
    border: '1px solid transparent',
    fontWeight: 600
  },
  danger: {
    background: 'var(--surface-card)',
    color: 'var(--state-error)',
    border: '1px solid var(--state-error-bg)',
    fontWeight: 500
  }
};

// 28px inside table rows, 34-36px in headers and panels, 44px touch targets on phones.
const BTN_SIZE: Record<BtnSize, CSSProperties> = {
  row: { height: 28, padding: '0 10px', fontSize: 12, borderRadius: 7 },
  md: { height: 36, padding: '0 14px', fontSize: 13, borderRadius: 8 },
  touch: { height: 44, padding: '0 14px', fontSize: 13, borderRadius: 8 }
};

export const btnStyle = (variant: BtnVariant = 'neutral', size: BtnSize = 'md'): CSSProperties => ({
  margin: 0,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  cursor: 'pointer',
  ...BTN_SIZE[size],
  ...BTN_VARIANT[variant]
});

type BtnProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: BtnVariant;
  size?: BtnSize;
  block?: boolean;
};

export function Btn({
  variant = 'neutral',
  size = 'md',
  block,
  style,
  disabled,
  children,
  ...rest
}: BtnProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      style={{
        ...btnStyle(variant, size),
        ...(block ? { width: '100%' } : null),
        ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : null),
        ...style
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      {children}
    </button>
  );
}

export function SectionCard({
  title,
  actions,
  children,
  className,
  testId
}: {
  title: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <section
      data-testid={testId}
      className={`overflow-hidden rounded-[10px] border border-line-subtle bg-surface-card ${className || ''}`}
    >
      <div className="flex items-center gap-2 border-b border-line-subtle bg-surface-sunken px-3.5 py-2">
        <h2 className={`m-0 ${LABEL_CLASS}`}>{title}</h2>
        {actions ? <div className="ml-auto flex items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

// Label / value pairs (the Teacher Payments "ledger").
export function Ledger({
  rows,
  labelWidth = 110,
  className
}: {
  rows: Array<[ReactNode, ReactNode]>;
  labelWidth?: number;
  className?: string;
}) {
  return (
    <dl
      className={`m-0 grid gap-x-3 gap-y-1.5 text-[12.5px] ${className || ''}`}
      style={{ gridTemplateColumns: `${labelWidth}px minmax(0,1fr)` }}
    >
      {rows.map(([k, v], i) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={i}>
          <dt className="text-content-muted">{k}</dt>
          <dd className="m-0 min-w-0 text-content-primary">{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

export interface SegOption<T extends string> {
  value: T;
  label: ReactNode;
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  fill,
  testId
}: {
  options: Array<SegOption<T>>;
  value: T;
  onChange: (v: T) => void;
  label: string;
  fill?: boolean;
  testId?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      data-testid={testId}
      className={`gap-0.5 rounded-lg border border-line-subtle bg-surface-sunken p-[3px] ${
        fill ? 'grid w-full' : 'inline-flex'
      }`}
      style={fill ? { gridTemplateColumns: `repeat(${options.length}, minmax(0,1fr))` } : undefined}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(o.value)}
            className="whitespace-nowrap rounded-md px-3 text-[13px] leading-5"
            style={{
              margin: 0,
              border: 0,
              padding: '4px 12px',
              background: on ? 'var(--surface-card)' : 'transparent',
              color: on ? 'var(--text-primary)' : 'var(--text-secondary)',
              fontWeight: on ? 600 : 400,
              boxShadow: on ? 'var(--shadow-card)' : 'none',
              cursor: 'pointer'
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Banner({
  tone,
  children,
  actions,
  role,
  testId
}: {
  tone: Exclude<Tone, 'outline' | 'brand'>;
  children: ReactNode;
  actions?: ReactNode;
  role?: string;
  testId?: string;
}) {
  return (
    <div
      role={role}
      data-testid={testId}
      className="flex flex-wrap items-center gap-2 rounded-lg py-1.5 pl-3 pr-1.5 text-xs"
      style={TONE[tone]}
    >
      <span className="min-w-0">{children}</span>
      {actions ? <span className="ml-auto flex items-center gap-1.5">{actions}</span> : null}
    </div>
  );
}

export type DialogVariant = 'center' | 'side';

/*
 * The one popup shell: centred dialog for short confirms, side panel for content
 * the agent reads while working the form. Below 640px both become a bottom sheet.
 * Esc closes, focus moves in on open and returns to the opener on close.
 */
export function SalesDialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  variant = 'center',
  width = 440,
  role = 'dialog',
  testId,
  headerExtra,
  describedBy,
  zIndex
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  variant?: DialogVariant;
  width?: number;
  role?: 'dialog' | 'alertdialog';
  testId?: string;
  headerExtra?: ReactNode;
  describedBy?: string;
  // Raise above a host popup (the SMS window sits at 10000) when opened from inside it.
  zIndex?: number;
}) {
  const panel = useRef<HTMLDivElement | null>(null);
  const opener = useRef<Element | null>(null);
  // Hosts pass inline closures; reading it from a ref keeps focus from re-running every render.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    opener.current = document.activeElement;
    const el = panel.current;
    const first = el?.querySelector<HTMLElement>(
      'button:not([data-dialog-close]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    (first || el)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const back = opener.current as HTMLElement | null;
      if (back && typeof back.focus === 'function') back.focus();
    };
  }, [open]);

  if (!open) return null;
  const side = variant === 'side';
  const node = (
    <div
      className={`fixed inset-0 z-[1300] flex max-sm:items-end max-sm:justify-stretch ${
        side ? 'items-stretch justify-end' : 'items-center justify-center'
      }`}
      style={{
        background: side ? 'var(--overlay-modal)' : 'var(--overlay-modal-strong)',
        ...(zIndex ? { zIndex } : null)
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role={role}
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        aria-describedby={describedBy}
        tabIndex={-1}
        data-testid={testId}
        className={`stl-root flex max-h-[92vh] flex-col overflow-hidden bg-surface-card text-[13px] text-content-primary outline-none max-sm:max-h-[85vh] max-sm:w-full max-sm:rounded-b-none max-sm:rounded-t-2xl ${
          side ? 'h-full rounded-none border-l border-line-subtle' : 'rounded-xl'
        }`}
        style={{
          width: side ? `min(${width}px, 100vw)` : `min(${width}px, 96vw)`,
          boxShadow: 'var(--shadow-modal)'
        }}
      >
        <span
          aria-hidden="true"
          className="mx-auto mt-2 hidden h-1 w-10 rounded bg-line-subtle max-sm:block"
        />
        <div className="flex items-start gap-2.5 border-b border-line-subtle px-4 py-3.5">
          <div className="min-w-0">
            <div className="text-[15px] font-semibold leading-5">{title}</div>
            {subtitle ? <div className="text-xs text-content-muted">{subtitle}</div> : null}
          </div>
          {headerExtra ? (
            <div className="ml-auto flex items-center gap-2">{headerExtra}</div>
          ) : null}
          <button
            type="button"
            data-dialog-close
            aria-label="Close"
            onClick={onClose}
            className="shrink-0 text-xl leading-none text-content-muted"
            style={{
              margin: 0,
              marginLeft: headerExtra ? 0 : 'auto',
              width: 32,
              height: 32,
              border: 0,
              background: 'transparent',
              borderRadius: 8
            }}
          >
            ×
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line-subtle bg-surface-sunken px-4 py-3 max-sm:pb-5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
  return createPortal(node, document.body);
}

// Settings-page field look (inputs and selects); inline because the CMS styles bare inputs.
export const fieldStyle = (width?: number | string, invalid?: boolean): CSSProperties => ({
  margin: 0,
  height: 30,
  boxSizing: 'border-box',
  padding: '0 8px',
  borderRadius: 7,
  border: `1px solid ${invalid ? 'var(--state-error)' : 'var(--input-border)'}`,
  background: 'var(--input-bg)',
  color: 'var(--input-text)',
  fontSize: 12.5,
  ...(width !== undefined ? { width } : null)
});

// Phones get 40px buttons; !important because btnStyle heights are inline.
export const TOUCH_CLASS = 'max-sm:!h-10';

// Pair with fieldStyle: the CMS forces `input, select { height: 46px !important }`.
export const FIELD_CLASS = '!h-[30px] max-sm:!h-10';

// On/off switch over a real checkbox, so keyboard and form semantics stay native.
export function Switch({
  checked,
  onChange,
  disabled,
  label,
  testId
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
  testId?: string;
}) {
  return (
    <label
      className="relative inline-flex items-center max-sm:h-10"
      style={{ cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}
    >
      <input
        type="checkbox"
        role="switch"
        aria-label={label}
        data-testid={testId}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className="relative inline-block h-[18px] w-[32px] shrink-0 rounded-full peer-focus-visible:shadow-[0_0_0_3px_var(--brand-primary-ring)]"
        style={{ background: checked ? 'var(--brand-primary)' : 'var(--border-emphasis)' }}
      >
        <span
          className="absolute top-[2px] h-[14px] w-[14px] rounded-full"
          style={{ left: checked ? 16 : 2, background: 'var(--text-on-brand)' }}
        />
      </span>
    </label>
  );
}
